<template>
  <div v-if="showAlert">
    <Alert
      :variant="type"
      :icon="AlertIcon"
      :arrow="hasAction"
      :class="hasAction ? 'cursor-pointer' : ''"
      @click="handleClick"
    >
      <template v-if="subscriptionOverdue || cancelledWithBalance">
        We are having a problem billing your account. Please update your payment
        method or call your payment provider for details on why the transaction
        failed.
      </template>

      <template v-else-if="subscriptionWillEnd">
        You have canceled your {{ billingStore.billing?.plan.name }} plan and it
        will end on {{ subscriptionRenewalDate }}. You will need to resubscribe
        to access your copyrighted work.
      </template>

      <template v-else-if="subscriptionCancelled">
        You canceled your membership. Reinstate your membership at any time and
        restore your benefits.
      </template>

      <template v-else-if="subscriptionRequiresAction">
        Your credit card provider requires you to authenticate your payment.
        Please proceed to complete authentication.
      </template>

      <template v-else-if="subscriptionUpgrade || subscriptionUpgradeBundle">
        You’ve reached your registration limit. Upgrade your plan to register
        more work.
      </template>

      <template v-else-if="subscriptionDowngrade">
        {{ billingStore.billing?.billingAlert?.description }}. Your new plan
        will take effect after your current subscription plan ends on
        {{ formatTimestampLocal(billingStore.billing?.plan.dateInfo?.date) }}.
      </template>

      <template v-else-if="subscriptionChargeback">
        A payment you previously made was disputed with your bank. Please
        contact your bank to reinstate the payment. Your account will remain
        disabled. Contact us to re-enable your account and settle your balance.
      </template>
    </Alert>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from '@/shared/stores/user'
import { formatTimestamp } from '@/shared/utils/datetime'
import { computed } from 'vue'
import { openModal, closeModal, Modals } from '@/shared/dialogs/modals'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { useBillingStore } from '@/features/billing/store'
import { usePaymentAuthentication } from '@/features/billing/composables/usePaymentAuthentication'
import { BillingAlertType } from '@/shared/graphql/legacy/generated'
import { useWebviewStore } from '@/shared/stores/webview'
import { Alert } from '@dacr/library'
import { AlertIcon } from '@dacr/icons'
import { useRouter } from 'vue-router'

const router = useRouter()

const userStore = useUserStore()
const billingStore = useBillingStore()
const webviewStore = useWebviewStore()
const { authenticatePayment } = usePaymentAuthentication()

const user = computed(() => userStore.user)

const subscriptionWillEnd = computed(
  () => billingStore.billing?.billingAlert?.type === BillingAlertType.WillCancel
)
const subscriptionCancelled = computed(
  () => billingStore.billing?.billingAlert?.type === BillingAlertType.Canceled
)
const subscriptionOverdue = computed(
  () => billingStore.billing?.billingAlert?.type === BillingAlertType.Overdue
)
const subscriptionRequiresAction = computed(
  () =>
    billingStore.billing?.billingAlert?.type === BillingAlertType.RequiresAction
)
const subscriptionUpgrade = computed(
  () => billingStore.billing?.billingAlert?.type === BillingAlertType.Upgrade
)
const subscriptionUpgradeBundle = computed(
  () =>
    billingStore.billing?.billingAlert?.type === BillingAlertType.UpgradeBundle
)
const subscriptionDowngrade = computed(
  () => billingStore.billing?.billingAlert?.type === BillingAlertType.Downgrade
)
const subscriptionChargeback = computed(
  () => billingStore.billing?.billingAlert?.type === BillingAlertType.Chargeback
)

// Cancelling no longer settles the balance, so a cancelled subscription can still owe —
// and its later registration charges can still fail. `billingAlert.type` is derived from
// the subscription status alone, so it reports Canceled/WillCancel and never Overdue for
// these users. When there is a balance the billing problem is the actionable one, so it
// wins the banner. (`WillCancel` is the common case: the alert only becomes `Canceled`
// once currentPeriodEnd has passed.)
const hasOutstandingBalance = computed(
  () => (user.value?.upcomingBilling?.outstandingTotal ?? 0) > 0
)

const cancelledWithBalance = computed(
  () =>
    (subscriptionCancelled.value || subscriptionWillEnd.value) &&
    hasOutstandingBalance.value
)

const type = computed(() => {
  if (subscriptionUpgrade.value || subscriptionUpgradeBundle.value) {
    return 'success'
  }

  return !cancelledWithBalance.value &&
    (subscriptionRequiresAction.value || subscriptionWillEnd.value)
    ? 'warning'
    : 'error'
})

const hasAction = computed(() => !subscriptionDowngrade.value)

const handleClick = () => {
  if (subscriptionOverdue.value || cancelledWithBalance.value) {
    return handlePayClick()
  }
  if (subscriptionWillEnd.value || subscriptionCancelled.value) {
    return handleResubscribeClick()
  }
  if (subscriptionRequiresAction.value) return handleRequiresActionClick()
  if (subscriptionUpgrade.value) return handleUpgradeClick()
  if (subscriptionUpgradeBundle.value) return handleUpgradeBundleClick()
  if (subscriptionChargeback.value) {
    window.open('https://help.dacr.com/hc/en-us/requests/new', '_blank')
  }
}

const showAlert = computed(() => billingStore.billing?.billingAlert?.isActive)

const subscriptionRenewalDate = computed(() =>
  formatTimestamp(billingStore.billing?.billingAlert?.date, {
    formatString: user.value?.dateFormat,
  })
)

const handleResubscribeClick = () => {
  if (!billingStore.canUpdateSubscription) {
    return openDialog(Dialog.IAP, {
      props: {
        type: 'change',
      },
    })
  }

  if (webviewStore.isActive) {
    return openDialog(Dialog.ResubscribeIAP)
  }

  openModal(Modals.Resubscribe)
}

const handlePayClick = () => {
  if (!billingStore.canUpdateSubscription && webviewStore.isActive) {
    return openDialog(Dialog.CantPay)
  }

  router.push('/settings/billing')

  openModal(Modals.Pay, {
    events: {
      success() {
        openModal(Modals.Success, {
          props: {
            title: 'Payment successful',
          },
          events: {
            success() {
              closeModal()
            },
          },
        })
      },
    },
  })
}

const handleRequiresActionClick = () => authenticatePayment()

const handleUpgradeClick = () => {
  openModal(Modals.UpgradeToPro, {
    props: {
      hasReachedLimit: true,
    },
  })
}

const handleUpgradeBundleClick = () => {
  const hasReachedLimit =
    (user.value?.totalFiles || 0) >= (user.value?.totalFilesLimit || 0)

  openModal(Modals.UpgradeBundleLimit, {
    props: {
      hasReachedLimit,
    },
  })
}

const formatTimestampLocal = (timestamp: number) => {
  return formatTimestamp(timestamp, {
    formatString: 'LLLL d, yyyy',
  })
}
</script>
